<template lang="pug">
v-container.pt-8.px-8(fluid)
  //- Top title
  v-row.mb-2(no-gutters align="center")
    v-col.shrink
      v-btn.ml-n3(text icon @click="goBack" height)
        v-icon(color="neutral700" :size="24") mdi-arrow-left
    v-col.ml-3
      .text-2xl.font-semibold.neutral700--text {{ isSalesReport ? 'Sales Report' : 'Closing Report' }}
  //- Tab header
  v-row.mt-2(no-gutters align="center")
    v-col(cols="12" md="8")
      v-tabs(v-model="reportTab" color="basic700" :height="44" :grow="isMobileScreen")
        template(v-for="tab in tabs")
          v-tab.text-sm.font-semibold.text-none.px-4.capitalize {{ tab.text }}
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center.justify-end
        v-btn(v-if="selectedTabId !== 'daily_panel'" @click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField && selectedTabId !== 'daily_panel'" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        customize-column-menu.column-menu(v-if="selectedTabId === 'transaction'" type="transaction_sales_report" :showTooltip="false" iconSize="21")
        customize-column-menu.column-menu(v-else-if="selectedTabId === 'billing_detail'" type="billing_detail_sales_report" :showTooltip="false" iconSize="21")
        customize-column-menu.column-menu(v-else-if="selectedTabId === 'daily'" type="daily_sales_report" :showTooltip="false" iconSize="21")
        customize-column-menu.column-menu(v-else-if="selectedTabId === 'sales_by_panel'" type="sales_by_panel_report" :showTooltip="false" iconSize="21")
        template(v-if="selectedTabId === 'daily_panel'")
          v-btn.button-sm.ml-4.white--text(color="neutral700" depressed @click="onOpenDailyPanelDateDialog") Download
        v-btn.button-sm.ml-4.white--text(v-else-if="selectedTabId === 'sales_by_panel'" color="neutral700" depressed :loading="isDownloading" :disabled="!canDownloadReport" @click="validateBackgroundDownloadProcess('pdf')") Download
        v-menu(v-else offset-y left rounded="lg")
          template(v-slot:activator="{ on }")
            v-btn.button-sm.ml-4.white--text(v-on="on" color="neutral700" depressed :loading="isDownloading" :disabled="!canDownloadReport") Download
          v-list(dense)
            v-list-item-group
              v-list-item(v-for="downloadType in downloadTypes" :key="downloadType" :value="downloadType" @click="validateBackgroundDownloadProcess(downloadType)")
                v-list-item-content
                  v-list-item-title.text-sm.neutral700--text {{ downloadType }}
  v-divider
  //- Filter
  transaction-detail-filter-row.my-4(v-if="selectedTabId === 'transaction'" :filters.sync="filters.transaction" ref="filter" @fetchedPaymentMethods="onFetchedPaymentMethods")
  billing-detail-filter-row.my-4(v-else-if="selectedTabId === 'billing_detail'" :filters.sync="filters.billingDetail" ref="filter" @fetchedPaymentMethods="onFetchedPaymentMethods")
  daily-filter-row.my-4(v-else-if="selectedTabId === 'daily'" :filters.sync="filters.daily" ref="filter")
  sales-by-panel-filter-row.my-4(v-else-if="selectedTabId === 'sales_by_panel'" :filters.sync="filters.salesByPanel" ref="filter")
  //- Daily Panel empty state
  template(v-if="selectedTabId === 'daily_panel'")
    yz-empty-state-card.mt-5.no-filter-height(icon="$yzi-calendar-plus-02" icon-color="yz-neutral-500" title="Apply date to generate report" description="Data will appear once report date is selected")
      template(v-slot:action)
        yz-btn(label="Select date" prepend-icon="mdi-plus" size="lg" @click="onOpenDailyPanelDateDialog")
  //- Sales by panel listing
  .pb-4(v-else-if="selectedTabId === 'sales_by_panel' && hasAllRequiredFilters")
    yz-empty-state-card.mt-5.no-filter-height(v-if="!isFetching && totalCount === 0" icon="$yzi-file-search-02" icon-color="yz-neutral-500" title="No data available" description="No records found for the selected filters")
    sales-by-panel-grouped-tables(v-else :headers="headers" :invoices="invoices" :panel-totals="panelTotals" :is-fetching="isFetching")
  //- Listing
  .data-table.pb-4(v-else-if="hasAllRequiredFilters" :class="[{ 'data-available': invoices.length > 0, 'data-is-ready': !isFetching, 'data-top': selectedTabId === 'billing_detail' }]")
    v-data-table(:headers="headers" :items="invoices" item-key="id" :items-per-page="pageSize + 1" hide-default-footer :loading="isFetching" disable-sort loader-height="4")
      template(v-slot:item.paymentMethod="{ item }")
        template(v-if="selectedTabId === 'transaction'")
          div(v-if="item.paymentMethod && item.paymentMethod.length > 0")
            v-row.my-1(v-for="(payment, paymentIdx) in item.paymentMethod" :key="`${payment.payment_method}-${paymentIdx}`" no-gutters)
              v-col(cols="9")
                div {{ payment.payment_method }}
              v-col(cols="3")
                div.text-right {{ payment.total | displayPriceRaw }}
          div(v-else) {{ item.paymentMethod }}
        template(v-if="selectedTabId === 'billing_detail'")
          div(v-if="item.paymentMethod && item.paymentMethod.length > 0")
            v-row.my-1(v-for="(payment, paymentIdx) in item.paymentMethod" :key="`${payment.payment_method}-${paymentIdx}`" no-gutters)
              v-col(cols="9")
                div {{ payment.payment_method }}
              v-col(cols="3")
                div.text-right {{ payment.total | displayPriceRaw }}
  //- No filters
  v-card.rounded-lg.border-dashed.pa-8.d-flex.flex-column.justify-center.align-center.no-filter-height(v-else outlined)
    v-sheet.round-full.pa-2.neutral300.d-flex.justify-center.align-center
      v-icon(size="22" color="neutral700") $yzi-file-search-02
    .text-md.mt-4.font-semibold Apply {{ requiredItemText }} filter to display data
    .text-md.neutral500--text Apply filters located at upper left corner of the table
  v-footer.white.footer(v-if="selectedTabId !== 'daily_panel' && hasAllRequiredFilters && totalCount > pageSize" app padless fixed)
    .d-flex.flex-column.grow.mb-2
      v-divider
      .d-flex.justify-end.mt-2.mx-4
        yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
  // Dialogs
  sales-report-in-background-process-dialog(:open.sync="downloadInBackground.open" :receiverEmails="downloadInBackground.receiverEmails")
  sales-report-download-restriction-dialog(:open.sync="downloadRestriction.open" :downloadType="downloadRestriction.downloadType")
  daily-sales-report-date-dialog(:open.sync="dailyPanelDateDialog" @apply="onApplyDailyPanelDate")
</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import TransactionDetailFilterRow from '@/components/clinic/reports/filters/TransactionDetailSalesReportFilterRow.vue'
import BillingDetailFilterRow from '@/components/clinic/reports/filters/BillingDetailSalesReportFilterRow.vue'
import DailyFilterRow from '@/components/clinic/reports/filters/DailySalesReportFilterRow.vue'
import SalesByPanelFilterRow from '@/components/clinic/reports/filters/SalesByPanelFilterRow.vue'
import SalesByPanelGroupedTables from '@/components/clinic/reports/SalesByPanelGroupedTables.vue'
import SalesReportInBackgroundProcessDialog from "@/components/clinic/dialogs/SalesReportInBackgroundProcessDialog.vue";
import SalesReportDownloadRestrictionDialog from "@/components/clinic/dialogs/SalesReportDownloadRestrictionDialog.vue";
import DailySalesReportDateDialog from "@/components/clinic/dialogs/DailySalesReportDateDialog.vue";

export default {
  components: {
    SalesReportInBackgroundProcessDialog, SalesReportDownloadRestrictionDialog, DailySalesReportDateDialog, CustomizeColumnMenu, TransactionDetailFilterRow, DailyFilterRow, BillingDetailFilterRow, SalesByPanelFilterRow, SalesByPanelGroupedTables
  },
  data() {
    return {
      reportTab: 0,
      searchText: null,
      showSearchField: false,
      filters: {
        transaction: [],
        billingDetail: [],
        daily: [],
        salesByPanel: []
      },
      isFetching: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      invoices: [],
      panelTotals: [],
      isDownloading: false,
      tabs: [
        {
          id: 'billing_detail',
          text: 'Billing Detail'
        },
        {
          id: 'transaction',
          text: 'Transaction Detail'
        },
        {
          id: 'daily',
          text: 'Daily Summary'
        },
        {
          id: 'daily_panel',
          text: 'Daily Report by Payment Method'
        },
        {
          id: 'sales_by_panel',
          text: 'Sales by Panel'
        },
      ],
      paymentMethods: null,
      selectedDownloadType: null,
      downloadTypes: ['pdf', 'csv'],
      downloadInBackground: {
        open: false,
        receiverEmails: null
      },
      downloadRestriction: {
        open: false,
        downloadType: null
      },
      dailyPanelDate: null,
      dailyPanelDateDialog: false
    }
  },
  watch: {
    filters: {
      handler() {
        if (this.hasAllRequiredFilters) {
          this.currentPage = 1
          this.fetchInvoices()
        }
      },
      deep: true
    },
    reportTab () {
      this.invoices = []
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      transactionSalesReportFields: 'clinic/getTransactionSalesReportFields',
      billingDetailSalesReportFields: 'clinic/getBillingDetailSalesReportFields',
      dailySalesReportFields: 'clinic/getDailySalesReportFields',
      salesByPanelReportFields: 'clinic/getSalesByPanelReportFields'
    }),
    isSalesReport () {
      return this.Auth('component', '/admin/clinic/reports/sales#view_sales_report')
    },
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    headers () {
      if (this.selectedTabId === 'daily_panel') return []

      const runningNumber = {
        id: 'runningNumber',
        field_name: '#',
        enable: true,
        type: 'system'
      }
      let fields
      // Transaction fields
      if (this.selectedTabId === 'transaction') {
        fields = [runningNumber, ...this.transactionSalesReportFields].filter(item => item.enable)
      }
      // Billing fields
      if (this.selectedTabId === 'billing_detail') {
        fields = [runningNumber, ...this.billingDetailSalesReportFields].filter(item => item.enable)
      }
      // Daily fields
      if (this.selectedTabId === 'daily') {
        fields = [runningNumber, ...this.dailySalesReportFields].filter(item => item.enable)
      }
      // Sales by panel fields
      if (this.selectedTabId === 'sales_by_panel') {
        fields = [runningNumber, ...this.salesByPanelReportFields].filter(item => item.enable)
      }

      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
        item.align = 'start'
        if (item.id === 'runningNumber') item.width = 10
        if (item.id === 'recordDate') item.width = 200
        if (item.id === 'noOfPatients') item.width = 200
        if (item.id === 'totalSales') item.width = 400
        if (item.id === 'amount') item.align = 'end'
        if (item.id === 'panel') item.align = 'end'
        if (item.id === 'cash') item.align = 'end'
        if (item.id === 'non_cash') item.align = 'end'
        if (item.id === 'due') item.align = 'end'
        if (item.id === 'outstanding') item.align = 'end'
        if (item.id === 'panel_charge') item.align = 'end'
        if (item.id === 'approved') item.align = 'end'
        if (item.id === 'rejected') item.align = 'end'
        if (item.id === 'admin_fee') item.align = 'end'
        if (item.id === 'received') item.align = 'end'
      })

      if (this.selectedTabId === 'sales_by_panel') {
        const weights = {
          runningNumber: 3,
          datetime: 9,
          invoice_no: 7,
          staff_details: 10,
          patient_details: 10,
          panel_charge: 8,
          approved: 8,
          rejected: 8,
          admin_fee: 8,
          received: 8,
          outstanding: 8
        }
        const totalWeight = fields.reduce((sum, item) => sum + (weights[item.id] || 0), 0)
        if (totalWeight > 0) {
          fields.forEach(item => {
            if (weights[item.id]) {
              item.width = `${((weights[item.id] / totalWeight) * 100).toFixed(2)}%`
            }
          })
        }
      }

      return [...fields]
    },
    params () {
      if (this.selectedTabId === 'daily_panel') return {}

      let params = {
        page: this.currentPage,
        page_size: this.pageSize,
        company: this.company.id,
        search: this.searchText || null,
        form_type: 'registration'
      }
      // Transaction filters
      if (this.selectedTabId === 'transaction') {
        params.report_type = 'transaction_detail'
        this.filters.transaction.forEach(filter => {
          params[filter.key] = filter.value
        })
      }
      // transaction details panel
      if (this.selectedTabId === 'billing_detail') {
        params.report_type = 'billing_detail'
        this.filters.billingDetail.forEach(filter => {
          params[filter.key] = filter.value
        })
      }
      // Daily filters
      if (this.selectedTabId === 'daily') {
        params.report_type = 'daily_summary'
        params.raw = true
        this.filters.daily.forEach(filter => {
          params[filter.key] = filter.value
        })
      }
      // Sales by panel filters
      if (this.selectedTabId === 'sales_by_panel') {
        params.report_type = 'sales_by_panel'
        this.filters.salesByPanel.forEach(filter => {
          params[filter.key] = filter.value
        })
      }
      // Date range filter
      if (params.date__gte && params.date__lte) {
        params.date_range = `${params.date__gte},${params.date__lte}`
        delete params.date__gte
        delete params.date__lte
      }

      return params
    },
    hasAllRequiredFilters () {
      if (this.selectedTabId === 'daily_panel') return false

      let requiredFilters
      let filterKeys
      // Transaction tab
      if (this.selectedTabId === 'transaction') {
        requiredFilters = ['date__gte', 'date__lte', 'time_range']
        filterKeys = this.filters.transaction.map(filter => filter.key)
      }
      // Billing detail tab
      if (this.selectedTabId === 'billing_detail') {
        requiredFilters = ['date__gte', 'date__lte', 'time_range']
        filterKeys = this.filters.billingDetail.map(filter => filter.key)
      }
      // Daily tab
      if (this.selectedTabId === 'daily') {
        requiredFilters = ['date__gte', 'date__lte']
        filterKeys = this.filters.daily.map(filter => filter.key)
      }
      // Sales by panel tab
      if (this.selectedTabId === 'sales_by_panel') {
        requiredFilters = ['date__gte', 'date__lte', 'time_range']
        filterKeys = this.filters.salesByPanel.map(filter => filter.key)
      }

      return requiredFilters.every(required => filterKeys.includes(required))
    },
    canDownloadReport () {
      if (this.selectedTabId === 'daily_panel') return false
      return this.hasAllRequiredFilters && this.invoices.length > 0
    },
    selectedTabId () {
      return this.tabs[this.reportTab].id
    },
    requiredItemText () {
      if (this.selectedTabId === 'transaction' || this.selectedTabId === 'billing_detail') return 'date and time'

      return 'date'
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    goBack () {
      return this.$router.push('/admin/clinic/settings')
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged : debounce(function () {
      this.fetchInvoices()
    }, 750),
    async fetchInvoices () {
      if (this.selectedTabId === 'daily_panel') return

      try {
        this.isFetching = true
        let url
        if (this.selectedTabId === 'transaction') url = '/transactions/sales_report'
        if (this.selectedTabId === 'billing_detail') url = '/invoices/sales_report'
        if (this.selectedTabId === 'daily') url = '/invoices/sales_report'
        if (this.selectedTabId === 'sales_by_panel') url = '/invoices/sales_by_panel_report'
        const { data } = await this.$axios.get(url, { params: this.params })
        if (data.results && data.count > 0) {
          this.invoices = data.results.map((result, index) => this.sanitizedResults(result, index))
          this.totalCount = data.count
          const total = this.sanitizedTotal(data.meta)
          if (total) {
            total._isTotal = true
            this.invoices.push(total)
          }
          if (this.selectedTabId === 'sales_by_panel' && data.meta && Array.isArray(data.meta.panels)) {
            this.panelTotals = data.meta.panels.map(p => ({
              name: p.name,
              panel_charge: this.sanitizePrice(p.total_panel_charge),
              approved: this.sanitizePrice(p.total_approved),
              rejected: this.sanitizePrice(p.total_rejected),
              admin_fee: this.sanitizePrice(p.total_admin_fee),
              received: this.sanitizePrice(p.total_received),
              outstanding: this.sanitizePrice(p.total_outstanding)
            }))
          } else {
            this.panelTotals = []
          }
        } else {
          this.invoices = []
          this.totalCount = 0
          this.panelTotals = []
        }
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    calculateRunningNumber (index) {
      const offset = (this.currentPage - 1) * this.pageSize
      return offset + index + 1
    },
    sanitizedResults (data, index) {
      let result = {...data}
      // Running number
      result.runningNumber = this.calculateRunningNumber(index)
      // Transaction items
      if (this.selectedTabId === 'transaction') {
        // Datetime
        result.datetime = this.$options.filters.convertServerDate2(result.datetime)
        // Pricing
        result.amount = this.sanitizePrice(result.amount)
        // Payment method
        result.paymentMethod = data.payments ? data.payments : [{ payment_method: data.payment_method, total: data.amount }]
      }
      // Billing items
      if (this.selectedTabId === 'billing_detail') {
        // Datetime
        result.datetime = this.$options.filters.convertServerDate2(result.datetime)
        // Pricing
        result.amount = this.sanitizePrice(result.amount)
        result.panel = this.sanitizePrice(result.panel)
        result.cash = this.sanitizePrice(result.cash)
        result.non_cash = this.sanitizePrice(result.non_cash)
        result.due = this.sanitizePrice(result.due)
        // Payment method
        result.paymentMethod = data.payments
        // Invoice no
        result.rid = `#${data.rid}`
      }
      // Daily items
      if (this.selectedTabId === 'daily') {
        // Datetime
        result.date = DateTime.fromISO(result.date).toFormat('dd/MM/yyyy')
        // Pricing
        result.sales = this.sanitizePrice(result.sales)
      }
      // Sales by panel items
      if (this.selectedTabId === 'sales_by_panel') {
        result.datetime = this.$options.filters.convertServerDate2(data.datetime)
        result.panel_charge = this.sanitizePrice(data.panel_charge)
        result.approved = this.sanitizePrice(data.approved)
        result.rejected = this.sanitizePrice(data.rejected)
        result.admin_fee = this.sanitizePrice(data.admin_fee)
        result.received = this.sanitizePrice(data.received)
        result.outstanding = this.sanitizePrice(data.outstanding)
      }

      return result
    },
    sanitizedTotal (meta) {
      if (this.selectedTabId === 'transaction') {
        return {
          amount: this.sanitizePrice(meta.total_amount),
          panel: this.sanitizePrice(meta.total_panel),
          cash: this.sanitizePrice(meta.total_cash),
          non_cash: this.sanitizePrice(meta.total_non_cash),
          outstanding: this.sanitizePrice(meta.total_outstanding),
          paymentMethod: meta.total_payments
        }
      }
      if (this.selectedTabId === 'billing_detail') {
        return {
          amount: this.sanitizePrice(meta.total_amount),
          panel: this.sanitizePrice(meta.total_panel),
          cash: this.sanitizePrice(meta.total_cash),
          non_cash: this.sanitizePrice(meta.total_non_cash),
          due: this.sanitizePrice(meta.total_outstanding),
          paymentMethod: meta.total_payments
        }
      }
      if (this.selectedTabId === 'daily') {
        return {
          sales: this.sanitizePrice(meta.total_sales),
          leads: meta.total_leads
        }
      }
      if (this.selectedTabId === 'sales_by_panel') {
        return {
          panel_charge: this.sanitizePrice(meta.total_panel_charge),
          approved: this.sanitizePrice(meta.total_approved),
          rejected: this.sanitizePrice(meta.total_rejected),
          admin_fee: this.sanitizePrice(meta.total_admin_fee),
          received: this.sanitizePrice(meta.total_received),
          outstanding: this.sanitizePrice(meta.total_outstanding)
        }
      }
    },
    sanitizePrice (price) {
      if (price === null || price === undefined) return '-'

      if (price === 0) return '0'

      return this.$options.filters.displayPriceRaw(price)
    },
    pageChanged () {
      this.fetchInvoices()
    },
    validateDateRangeForDownload () {
      // Only apply to billing_detail tab
      if (this.selectedTabId !== 'billing_detail') return true

      // Get date range from filters
      const dateGte = this.filters.billingDetail.find(f => f.key === 'date__gte')?.value
      const dateLte = this.filters.billingDetail.find(f => f.key === 'date__lte')?.value

      if (!dateGte || !dateLte) return true

      // Calculate days difference using Luxon
      const startDate = DateTime.fromISO(dateGte)
      const endDate = DateTime.fromISO(dateLte)
      const daysDiff = endDate.diff(startDate, 'days').days

      // Validate based on download type
      if (this.selectedDownloadType === 'pdf' && daysDiff > 31) {
        return false
      }
      if (this.selectedDownloadType === 'csv' && daysDiff > 365) {
        return false
      }
      return true
    },
    async validateBackgroundDownloadProcess (downloadType) {
      // Set the download type from the clicked item
      this.selectedDownloadType = downloadType
      // Validate date range for billing_detail downloads
      if (!this.validateDateRangeForDownload()) {
        this.downloadRestriction.downloadType = this.selectedDownloadType
        this.downloadRestriction.open = true
        this.selectedDownloadType = null
        return
      }

      try {
        this.isDownloading = true
        let url
        if (this.selectedTabId === 'transaction') url = '/transactions/validate_report_processing'
        if (this.selectedTabId === 'billing_detail') url = '/invoices/validate_report_processing'
        if (this.selectedTabId === 'daily') url = '/invoices/validate_report_processing'
        if (this.selectedTabId === 'sales_by_panel') url = '/invoices/validate_report_processing'
        const { data } = await this.$axios.get(url, { params: this.params })
        const { is_process_in_background } = data
        if (is_process_in_background) {
          await this.handleBackgroundDownloadReport()
        } else {
          await this.handleDirectDownloadReport()
        }
      } catch (e) {
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
      }
    },
    async handleBackgroundDownloadReport () {
      try {
        this.isDownloading = true
        // Url setup
        let endpoint
        if (this.selectedTabId === 'transaction') endpoint = '/transactions/sales_report'
        if (this.selectedTabId === 'billing_detail') endpoint = '/invoices/sales_report'
        if (this.selectedTabId === 'daily') endpoint = '/invoices/sales_report'
        if (this.selectedTabId === 'sales_by_panel') endpoint = '/invoices/sales_by_panel_report'
        // Prepare data
        const params = {}
        const omitKey = ['page', 'page_size', 'search', 'raw']
        for (const objKey in this.params) {
          if (!omitKey.includes(objKey) && this.params[objKey]) {
            params[objKey] = this.params[objKey]
          }
        }
        // Download file type
        params.export = this.selectedDownloadType
        const { data } = await this.$axios.get(endpoint, { params })
        const { receiver_emails } = data
        this.downloadInBackground.receiverEmails = receiver_emails
        this.downloadInBackground.open = true
      } catch (e) {
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.selectedDownloadType = null
        this.isDownloading = false
      }
    },
    async handleDirectDownloadReport () {
      this.isDownloading = true
      // Get key first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
        return
      }
      // Open download url
      let baseUrl
      if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      } else {
        baseUrl = 'https://export-test.yezza.io'
      }
      // Url setup
      let url
      if (this.selectedTabId === 'transaction') url = `${baseUrl}/transactions/sales_report`
      if (this.selectedTabId === 'billing_detail') url = `${baseUrl}/invoices/sales_report`
      if (this.selectedTabId === 'daily') url = `${baseUrl}/invoices/sales_report`
      if (this.selectedTabId === 'sales_by_panel') url = `${baseUrl}/invoices/sales_by_panel_report`

      url = new URL(url)
      // Set key
      url.searchParams.set('key', key)
      // Prepare data
      const omitKey = ['page', 'page_size', 'search', 'raw']
      for (const objKey in this.params) {
        if (!omitKey.includes(objKey) && this.params[objKey]) {
          url.searchParams.set(objKey, this.params[objKey])
        }
      }
      // Download file type
      url.searchParams.set('export', this.selectedDownloadType)
      window.open(url, this.selectedDownloadType === 'pdf' ? '_blank' : '_self')
      this.selectedDownloadType = null
      this.isDownloading = false
    },
    onOpenDailyPanelDateDialog () {
      this.dailyPanelDateDialog = true
    },
    async onApplyDailyPanelDate (date) {
      this.dailyPanelDate = date
      await this.validateDailyPanelDownloadProcess(date)
    },
    buildDailyPanelParams (date) {
      return {
        company: this.company.id,
        form_type: 'registration',
        report_type: 'billing_by_payment',
        date_range: `${date},${date}`
      }
    },
    async validateDailyPanelDownloadProcess (date) {
      try {
        this.isDownloading = true
        const params = this.buildDailyPanelParams(date)
        const { data } = await this.$axios.get('/invoices/validate_report_processing', { params })
        const { is_process_in_background } = data
        if (is_process_in_background) {
          await this.handleDailyPanelBackgroundDownload(date)
        } else {
          await this.handleDailyPanelDownload(date)
        }
      } catch (e) {
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
      }
    },
    async handleDailyPanelBackgroundDownload (date) {
      try {
        this.isDownloading = true
        const params = this.buildDailyPanelParams(date)
        params.export = 'pdf'
        const { data } = await this.$axios.get('/invoices/sales_report', { params })
        const { receiver_emails } = data
        this.downloadInBackground.receiverEmails = receiver_emails
        this.downloadInBackground.open = true
      } catch (e) {
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDownloading = false
      }
    },
    async handleDailyPanelDownload (date) {
      this.isDownloading = true
      // Get key first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
        return
      }
      // Open download url
      let baseUrl
      if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      } else {
        baseUrl = 'https://export-test.yezza.io'
      }
      let url = new URL(`${baseUrl}/invoices/sales_report`)
      url.searchParams.set('key', key)
      const params = this.buildDailyPanelParams(date)
      for (const objKey in params) {
        url.searchParams.set(objKey, params[objKey])
      }
      url.searchParams.set('export', 'pdf')
      window.open(url, '_blank')
      this.showMessage(['success', 'Report downloaded successfully'])
      this.isDownloading = false
    },
    onFetchedPaymentMethods ({ results }) {
      this.paymentMethods = results
    }
  },
}
</script>

<style scoped>
.no-filter-height {
  height: 40dvh;
}

::v-deep .data-table.data-is-ready table tbody tr td:nth-child(2),
::v-deep .data-table.data-is-ready table thead tr th:nth-child(2) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  z-index: 10;
  background: white;
}

::v-deep table tbody tr:nth-last-child(2) td {
  border-bottom: 1px solid transparent !important;
}

::v-deep .data-table.data-available table tbody tr:last-child td {
  border-top: 1px solid black;
}

.footer,
.column-menu {
  z-index: 11;
}

::v-deep .data-table.data-top table tbody tr td {
  vertical-align: top !important;
}

</style>